import { parseAuthCookies } from "./utils";
import { decodeJwt } from "oidc-spa/tools/decodeJwt";
import { signal } from "@preact/signals-react";
import { log, LogTag } from "./logger";

const tag: LogTag = "auth";

export interface TokenProvider {
  getTokenData(): Promise<{ jwt: string | null; seed: string | null; token: string }>;
}

// Global token provider - defaults to null, will use original cookie logic if not set
let globalTokenProvider: TokenProvider | null = null;

// Signals to store tokens
const tokenSignal = signal<string | null>(null);
const decodedTokenSignal = signal<object | null>(null);

/**
 * Set the global token provider for authentication
 * @param provider - The token provider to use, or null to use default cookie-based logic
 */
export function setTokenProvider(provider: TokenProvider | null): void {
  globalTokenProvider = provider;
  // Clear cached tokens when provider changes
  tokenSignal.value = null;
  decodedTokenSignal.value = null;
  
  // If provider is set, fetch token immediately
  if (provider) {
    refreshToken();
  }
}

/**
 * Get the current token provider
 */
export function getTokenProvider(): TokenProvider | null {
  return globalTokenProvider;
}

/**
 * Set token values in signals
 */
function setTokenValues(token: string | null): void {
  tokenSignal.value = token;
  decodedTokenSignal.value = token ? decodeJwt(token) : null;
}

/**
 * Refresh token from provider or cookies
 */
async function refreshToken(): Promise<void> {
  try {
    let token: string | null = null;

    if (globalTokenProvider) {
      const tokenData = await globalTokenProvider.getTokenData();
      token = tokenData.token;
    } else {
      // Original cookie-based logic
      const cookies = parseAuthCookies();
      token = cookies["Api.Token"];
    }

    setTokenValues(token);
  } catch (error) {
    log.error(tag, "Error refreshing token:", error);
    setTokenValues(null);
  }
}

/**
 * Ensure tokens are loaded in signals (synchronous fallback to cookies)
 */
function ensureTokensLoaded(): void {
  // If signal is empty and no token provider, try to get from cookies synchronously
  if (tokenSignal.value == null && !globalTokenProvider) {
    try {
      const cookies = parseAuthCookies();
      const token = cookies["Api.Token"];
      setTokenValues(token);
    } catch (error) {
      log.error(tag, "Error getting token from cookies:", error);
    }
  }
}

/**
 * Get the raw JWT token string (synchronous)
 */
export function getToken(): string | null {
  ensureTokensLoaded();
  return tokenSignal.value;
}

/**
 * Get the decoded API token (synchronous)
 */
export function getApiToken(): object | null {
  ensureTokensLoaded();
  return decodedTokenSignal.value;
}

/**
 * Get the raw JWT token string asynchronously (for use with token providers)
 */
export async function getTokenAsync(): Promise<string | null> {
  await refreshToken();
  return tokenSignal.value;
}

/**
 * Get the decoded API token asynchronously (for use with token providers)
 */
export async function getApiTokenAsync(): Promise<object | null> {
  await refreshToken();
  return decodedTokenSignal.value;
}
